import axios, { AxiosRequestConfig } from 'axios';
import { createApi } from '@reduxjs/toolkit/query/react'
import { AppVersionModel, LoginCredential, SecretaryTaskModel, BusinessDomainModel, SubDomainModel, SubscriptionTypeModel, SubscriptionModel, BusinessDataModel,
    AddressModel, ContactModel, EmployeeModel, QuestionModel, AnswerModel, SecretaryTasksAndQuestionsModel, SecretaryTasksAndQuestionsAndAnswersModel, ServiceModel,
    BusinessDomainInformation, ConversationMetaFilterModel, BookingModel, BookingFilterModel, BusinessModel, BookingSaveModel,
    CreateBusinessAnswerModel, AiResponseModel, AvailablePhoneNumberModel, PhoneNumberModel, BusinessHourModel, CustomerModel, ClaimModel, CustomerPolicyModel,
    ConversationMetaModel, IncidentModel, TranscriptMessageModel, PromptVersionDetail, PromptVersionRow, PromptExtendedModel, AssistantTypeModel, SkillModel, SkillSetModel,
    PasswordLoginRequest, PasswordLoginResponse, LoginTypes, ForgotPasswordRequest, ResetPasswordRequest, ChangePasswordRequest,
    DashboardActionCommunicationsResponseModel, DashboardMetricsModel, WorkItemModel, ConversationReviewModel, DeepKnowledgeDocumentModel, DeepKnowledgeChunkModel,
    SupportTicketModel, SupportTicketDetailModel,
    BusinessKnowledgeEntryModel, BusinessKnowledgeSearchRequestModel, BusinessKnowledgeSearchResponseModel, BusinessKnowledgeWebsiteRescanRequestModel, BusinessKnowledgeWebsiteRescanResponseModel,
    BusinessNotificationConfigDocumentModel, BusinessNotificationConfigSaveRequestModel, CustomerCsvImportResultModel,
    SimulatedCallerReplyRequestModel, SimulatedCallerReplyResponseModel, SimulatedCallerSpeechRequestModel, AiTestCallerSetupModel, BillingSummaryModel, BillingBalanceControlsSaveRequestModel, BillingBalanceControlsSaveResponseModel, BillingPaymentProviderStatusModel, BillingPaymentRequestCreateRequestModel, BillingPaymentRequestCreateResponseModel, BillingPaymentMethodSetupResponseModel, TailoredSetupInquiryRequestModel, TailoredSetupInquiryResponseModel,
    QuickSetupCompleteRequestModel, QuickSetupCompleteResponseModel, QuickSetupGooglePlaceSearchResultModel, QuickSetupGooglePlaceSnapshotModel, SetupFreeTestCallAllowanceModel,
    AgencyBusinessListResponseModel, AgencySwitchBusinessRequestModel, AgencySwitchBusinessResponseModel } from '../models';
import type {
    BusinessPromptSaveRequest,
    BusinessPromptSaveResponse,
    DpaIdentityConfig,
    DpaVerificationConnectorTestRequest,
    DpaVerificationConnectorTestResponse,
    PromptTagDefinition
} from '../components/details/AiConfigNew/types';
import { RootState } from '../app/store';
import { logout } from '../helpers/logout';
import { setLogin } from '../reducers/globalSlice';
import { apiBaseUrl } from '../config/api';
import { getSharedFreshGoogleIdToken, noteAuthenticatedCredential, shouldHandleSessionExpiry } from './sessionExpiry';

const ApiRequestTimeoutMs = 15000;
const QuickSetupGenerationTimeoutMs = 120000;

const toQueryError = (err: any) => ({
    status: err?.code === 'ECONNABORTED' ? 'TIMEOUT_ERROR' : err?.response?.status ?? err?.status ?? 'FETCH_ERROR',
    data: err?.response?.data ?? err?.data,
    error: err?.code === 'ECONNABORTED' ? 'The API did not respond in time.' : err?.message,
});

const axiosBaseQuery =
  ({ baseUrl }: { baseUrl: string }) =>
  async (
    { url, method, data, params, responseType, timeoutMs, headers }: {
        url: string;
        method: string;
        data?: any;
        params?: any;
        responseType?: AxiosRequestConfig['responseType'];
        timeoutMs?: number;
        headers?: AxiosRequestConfig['headers'];
    },
    { getState, dispatch }: any
  ) => {
    const state = getState() as RootState;
    let token = state.global.credentialResponse?.credential as string | undefined;
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    const isAuthRequest = normalizedUrl.startsWith('/ClientLogin/');

    noteAuthenticatedCredential(token);

    const doRequest = async (auth?: string) =>
      axios({
        url: baseUrl + url,
        method,
        data,
        params,
        responseType,
        timeout: timeoutMs ?? ApiRequestTimeoutMs,
        headers: {
          ...headers,
          ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
        },
      });

    const runRequest = async (auth?: string) => {
      const response = await doRequest(auth);
      return { data: response.data };
    };

    try {
      // first attempt with current token
      return await runRequest(token);
    } catch (err: any) {
      // only handle 401 once
      if (err?.response?.status === 401) {
        const currentState = getState() as RootState;
        const currentCredential = currentState.global.credentialResponse?.credential as string | undefined;
        const currentLoginType = currentState.global.credentialResponse?.loginType ?? LoginTypes.SsoGoogle;

        // Don't treat login/register failures (or anonymous requests) as "session expired".
        if (isAuthRequest || !currentCredential) {
          return { error: toQueryError(err) };
        }

        noteAuthenticatedCredential(currentCredential);

        // Another request may already have refreshed the credential; reuse it instead of
        // continuing down the expired-session path with the stale token that failed.
        if (token && currentCredential !== token) {
          return await runRequest(currentCredential);
        }

        if (currentLoginType === LoginTypes.SsoGoogle) {
          const newToken = await getSharedFreshGoogleIdToken(getFreshGoogleIdToken);
          const latestCredential = (getState() as RootState).global.credentialResponse?.credential as string | undefined;

          if (token && latestCredential && latestCredential !== token) {
            noteAuthenticatedCredential(latestCredential);
            return await runRequest(latestCredential);
          }

          if (newToken) {
            // update store (keep existing fields, swap credential)
            const prev = (getState() as RootState).global.credentialResponse ?? {};
            dispatch(setLogin({ ...prev, credential: newToken }));
            noteAuthenticatedCredential(newToken);
            // retry with fresh token
            return await runRequest(newToken);
          }
        }
        // no new token or unsupported login type → log out
        const latestCredential = (getState() as RootState).global.credentialResponse?.credential as string | undefined;
        if (shouldHandleSessionExpiry(token, latestCredential)) {
          alert('Your session has expired. Please log in again.');
          logout();
        }
      }
      return { error: toQueryError(err) };
    }
  };

type DraftDataSaveRequest = {
  businessId: number;
  keyName: string;
  data: string;
  callSid?: string;
  sessionUpdateArea?: string;
  sessionUpdateValue?: string;
};

type DraftDataClearRequest = {
  businessId: number;
  keyName: string;
  callSid?: string;
  sessionUpdateArea?: string;
  sessionUpdateValue?: string;
};

type DraftDataResponse = {
  data: string;
  lastUpdatedAt?: string;
};

type PromptsAndAssistantsResponse = {
  prompts: PromptExtendedModel[];
  assistants: AssistantTypeModel[];
};

type InvokeFunctionInfo = {
  invokeFunctionId: number;
  invokeServiceId: number;
  functionName: string;
  functionDescription: string;
  permissionLevel?: number | null;
};

type PromptServiceInfo = {
  invokeServiceId: number;
  serviceName: string;
  isRequired: boolean;
  isSelected: boolean;
  functions: InvokeFunctionInfo[];
};

  declare global {
  interface Window {
    google?: any;
  }
}

/**
 * Attempts to silently obtain a fresh Google ID token using GIS One Tap.
 * Returns the token string or null if it can't.
 */
async function getFreshGoogleIdToken(): Promise<string | null> {
  if (!window.google?.accounts?.id) {
    // If you load GIS elsewhere (index.html), you can early-return null here.
    // Otherwise, try to initialize anyway; if the script isn't present this will no-op.
    return null;
  }

  return new Promise((resolve) => {
    let resolved = false;

    const finish = (val: string | null) => {
      if (!resolved) {
        resolved = true;
        resolve(val);
      }
    };

    // Initialize with your client id
    window.google.accounts.id.initialize({
      client_id: '603581137848-7hic62rk13in9b24emmgs4hbsd9e1cme.apps.googleusercontent.com',
      callback: (response: any) => {
        // response.credential is the ID token
        finish(response?.credential ?? null);
      },
      auto_select: true, // allows silent issuance when possible
      cancel_on_tap_outside: false,
    });

    // Prompt tries to issue silently; if it can't, we'll get a skipped/not-displayed state
    window.google.accounts.id.prompt((notification: any) => {
      // If a credential came via callback, finish() already resolved.
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // No token available silently.
        finish(null);
      }
      // Otherwise, leave resolution to the callback above.
    });

    // Safety timeout (optional)
    setTimeout(() => finish(null), 4000);
  });
}


// Define our single API slice object
export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: axiosBaseQuery({ baseUrl: apiBaseUrl }),
    tagTypes: [
        'BusinessCatalogCallHandling',
        'BusinessCatalogInboundSpamProtection',
        'BusinessCatalogDailyCallSummary',
        'BusinessCatalogOfferings',
        'BusinessCatalogOfferingCategories',
        'BusinessCatalogSetupSnapshot',
        'AiTestCallerSetup',
        'BusinessServices',
        'BusinessIntegrations',
        'ClientFollowUps',
    ],

    // The "endpoints" represent operations and requests for this server
    endpoints: builder => ({

        getCallLog: builder.query({
            query: () => ({
                url: `/Twilio/GetCallLog`,
                method: 'GET',
                params: null
            }),
        }),

        getAppVersion: builder.query<AppVersionModel, void>({
            query: () => ({
                url: `/App/GetVersion`,
                method: 'GET',
                params: { _cacheBust: Date.now() },
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                },
            }),
        }),

        submitTailoredSetupInquiry: builder.mutation<TailoredSetupInquiryResponseModel, TailoredSetupInquiryRequestModel>({
            query: (body) => ({
                url: '/Contact/SubmitTailoredSetupInquiry',
                method: 'POST',
                data: body,
            }),
        }),

        getPortalBusinessData: builder.query<BusinessDataModel, void>({
            query: () => ({
                url: '/ClientLogin/GetPortalBusinessData',
                method: 'GET',
            }),
        }),

        completeSimpleSetup: builder.mutation<QuickSetupCompleteResponseModel, QuickSetupCompleteRequestModel>({
            query: (body) => ({
                url: '/QuickSetup/CompleteSimpleSetup',
                method: 'POST',
                data: body,
                timeoutMs: QuickSetupGenerationTimeoutMs,
            }),
        }),

        searchQuickSetupGooglePlaces: builder.query<QuickSetupGooglePlaceSearchResultModel[], string>({
            query: (queryText) => ({
                url: '/QuickSetup/SearchGooglePlaces',
                method: 'GET',
                params: { query: queryText },
            }),
        }),

        getQuickSetupGooglePlace: builder.query<QuickSetupGooglePlaceSnapshotModel, string>({
            query: (placeId) => ({
                url: '/QuickSetup/GetGooglePlace',
                method: 'GET',
                params: { placeId },
            }),
        }),

        getSetupFreeTestCallAllowance: builder.query<SetupFreeTestCallAllowanceModel, number | void>({
            query: () => ({
                url: '/SetupFreeTestCalls/GetAllowance',
                method: 'GET',
            }),
        }),

        getAgencyBusinesses: builder.query<AgencyBusinessListResponseModel, void>({
            query: () => ({
                url: '/Agency/ListBusinesses',
                method: 'GET',
            }),
        }),

        switchAgencyBusiness: builder.mutation<AgencySwitchBusinessResponseModel, AgencySwitchBusinessRequestModel>({
            query: (body) => ({
                url: '/Agency/SwitchBusiness',
                method: 'POST',
                data: body,
            }),
        }),

        authenticateGoogleLogin: builder.mutation<BusinessDataModel, LoginCredential>({
            query: (credentialData) => ({
                url: '/ClientLogin/AuthenticateGoogleLogin',
                method: 'POST',
                data: credentialData,
            }),
        }),
        registerUsernameAndPassword: builder.mutation<PasswordLoginResponse, PasswordLoginRequest>({
            query: (body) => ({
                url: '/ClientLogin/RegisterUsernameAndPassword',
                method: 'POST',
                data: body,
            }),
        }),
        authenticateUsernameAndPassword: builder.mutation<PasswordLoginResponse, PasswordLoginRequest>({
            query: (body) => ({
                url: '/ClientLogin/AuthenticateUsernameAndPassword',
                method: 'POST',
                data: body,
            }),
        }),
        forgotPasswordUsernameAndPassword: builder.mutation<string, ForgotPasswordRequest>({
            query: (body) => ({
                url: '/ClientLogin/ForgotPasswordUsernameAndPassword',
                method: 'POST',
                data: body,
            }),
        }),
        resetPasswordUsernameAndPassword: builder.mutation<string, ResetPasswordRequest>({
            query: (body) => ({
                url: '/ClientLogin/ResetPasswordUsernameAndPassword',
                method: 'POST',
                data: body,
            }),
        }),
        changePassword: builder.mutation<string, ChangePasswordRequest>({
            query: (body) => ({
                url: '/ClientLogin/ChangePassword',
                method: 'POST',
                data: body,
            }),
        }),
        // Endpoint to create or update a service
        saveService: builder.mutation({
            query: (serviceData) => ({
                url: '/Business/SaveService',
                method: 'POST',
                data: serviceData,
            }),
        }),

        ask: builder.mutation<AiResponseModel, any>({
            query: (aiQuestionData) => ({
                url: '/Ai/Ask',
                method: 'POST',
                data: aiQuestionData,
            }),
        }),
        getAiTestCallerSetup: builder.query<AiTestCallerSetupModel, number>({
            query: (businessId) => ({
                url: `/Ai/GetAiTestCallerSetup?businessId=${businessId}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, businessId) => [{ type: 'AiTestCallerSetup', id: businessId }],
        }),
        saveAiTestCallerSetup: builder.mutation<AiTestCallerSetupModel, AiTestCallerSetupModel>({
            query: (request) => ({
                url: '/Ai/SaveAiTestCallerSetup',
                method: 'PUT',
                data: request,
            }),
            invalidatesTags: (_result, _error, request) => [{ type: 'AiTestCallerSetup', id: request.businessId }],
        }),
        generateSimulatedCallerReply: builder.mutation<SimulatedCallerReplyResponseModel, SimulatedCallerReplyRequestModel>({
            query: (request) => ({
                url: '/Ai/GenerateSimulatedCallerReply',
                method: 'POST',
                data: request,
            }),
        }),
        generateSimulatedCallerSpeech: builder.mutation<Blob, SimulatedCallerSpeechRequestModel>({
            query: (request) => ({
                url: '/Ai/GenerateSimulatedCallerSpeech',
                method: 'POST',
                data: request,
                responseType: 'blob',
            }),
        }),
        generateVoicePreviewSpeech: builder.mutation<Blob, SimulatedCallerSpeechRequestModel>({
            query: (request) => ({
                url: '/Ai/GenerateVoicePreviewSpeech',
                method: 'POST',
                data: request,
                responseType: 'blob',
            }),
        }),

        getPromptVersions: builder.query<PromptVersionRow[], number>({
            query: (promptId) => ({
                url: `/Prompt/GetVersions/${promptId}`,
                method: 'GET',
            }),
        }),

        getPromptVersion: builder.query<PromptVersionDetail, { promptId: number; versionNo: number }>({
            query: ({ promptId, versionNo }) => ({
                url: `/Prompt/GetVersion/${promptId}/${versionNo}`,
                method: 'GET',
            }),
        }),

        savePromptBatch: builder.mutation<void, { items: { promptId: number; aiPrompt: string }[]; actorClientLoginId: number }>({
            query: (body) => ({
                url: '/Prompt/SaveBatch',
                method: 'POST',
                data: body,
            }),
        }),

        savePromptPackage: builder.mutation<BusinessPromptSaveResponse, BusinessPromptSaveRequest>({
            query: (body) => ({
                url: '/Prompt/SavePackage',
                method: 'POST',
                data: body,
            }),
        }),

        saveDraftData: builder.mutation<void, DraftDataSaveRequest>({
            query: (body) => ({
                url: '/DraftData/SaveDraft',
                method: 'POST',
                data: body,
            }),
        }),

        clearDraftData: builder.mutation<void, DraftDataClearRequest>({
            query: (body) => ({
                url: '/DraftData/ClearDraft',
                method: 'POST',
                data: body,
            }),
        }),

        getDraftData: builder.query<DraftDataResponse, { businessId: number; keyName: string }>({
            query: ({ businessId, keyName }) => ({
                url: `/DraftData/GetDraft?businessId=${businessId}&keyName=${encodeURIComponent(keyName)}`,
                method: 'GET',
            }),
        }),

        getPromptsAndAssistants: builder.query<PromptsAndAssistantsResponse, number>({
            query: (businessId) => ({
                url: `/Prompt/GetPromptsAndAssistants/${businessId}`,
                method: 'GET',
            }),
        }),

        getPromptTagDefinitions: builder.query<PromptTagDefinition[], number>({
            query: (businessDomainId) => ({
                url: `/Prompt/GetPromptTagDefinitions/${businessDomainId}`,
                method: 'GET',
            }),
        }),

        getDpaIdentityConfig: builder.query<DpaIdentityConfig, number>({
            query: (businessId) => ({
                url: `/Prompt/GetDpaIdentityConfig/${businessId}`,
                method: 'GET',
            }),
        }),

        saveDpaIdentityConfig: builder.mutation<DpaIdentityConfig, DpaIdentityConfig>({
            query: (body) => ({
                url: '/Prompt/SaveDpaIdentityConfig',
                method: 'POST',
                data: body,
            }),
        }),

        testDpaVerificationConnector: builder.mutation<DpaVerificationConnectorTestResponse, DpaVerificationConnectorTestRequest>({
            query: (body) => ({
                url: '/Prompt/TestDpaVerificationConnector',
                method: 'POST',
                data: body,
            }),
        }),

        getBusinessNotificationConfig: builder.query<BusinessNotificationConfigDocumentModel, number>({
            query: (businessId) => ({
                url: `/Prompt/GetBusinessNotificationConfig/${businessId}`,
                method: 'GET',
                params: { assistantMode: 'customer' },
            }),
        }),

        saveBusinessNotificationConfig: builder.mutation<BusinessNotificationConfigDocumentModel, BusinessNotificationConfigSaveRequestModel>({
            query: (body) => ({
                url: '/Prompt/SaveBusinessNotificationConfig',
                method: 'POST',
                data: body,
            }),
        }),

        getPromptServices: builder.query<PromptServiceInfo[], { promptId: number; businessDomainId: number }>({
            query: ({ promptId, businessDomainId }) => ({
                url: `/InvokeService/GetPromptServices?promptId=${promptId}&businessDomainId=${businessDomainId}`,
                method: 'GET',
            }),
        }),

        revertPrompt: builder.mutation<void, { promptId: number; versionNo: number; actorClientLoginId: number }>({
            query: ({ promptId, versionNo, actorClientLoginId }) => ({
                url: `/Prompt/Revert/${promptId}/${versionNo}`,
                method: 'POST',
                data: { actorClientLoginId },
            }),
        }),

        createFlowPrompt: builder.mutation<PromptExtendedModel, { businessId: number; assistantTypeId: number; flowName: string; aiPrompt: string; actorClientLoginId: number }>({
            query: (body) => ({
                url: '/Prompt/CreateFlow',
                method: 'POST',
                data: body,
            }),
        }),

        deleteFlowPrompt: builder.mutation<void, { promptId: number; actorClientLoginId: number }>({
            query: ({ promptId, actorClientLoginId }) => ({
                url: `/Prompt/DeleteFlow/${promptId}`,
                method: 'DELETE',
                data: { actorClientLoginId },
            }),
        }),

        clearCallContext: builder.mutation({
            query: (callSid) => ({
                url: `/Ai/ClearCallContext?callSid=${callSid}`,
                method: 'POST',
                data: callSid,
            }),
        }),

        // Endpoint to fetch existing services
        getServices: builder.query<ServiceModel[], number>({
            query: (businessId) => ({
                url: `/Business/GetServices?businessId=${businessId}`,
                method: 'GET'
            }),
        }),

        getPolicies: builder.query<CustomerPolicyModel[], number>({
            query: (customerId) => ({
                url: `/Insurance/GetPolicies?customerId=${customerId}`,
                method: 'GET'
            }),
        }),

        getClaims: builder.query<ClaimModel[], number>({
            query: (customerId) => ({
                url: `/Insurance/GetClaims?customerId=${customerId}`,
                method: 'GET'
            }),
        }),

        getIncidents: builder.query<IncidentModel[], number>({
            query: (customerId) => ({
                url: `/Insurance/GetIncidents?customerId=${customerId}`,
                method: 'GET'
            }),
        }),

        // Fetch phone numbers
        getBusinessPhoneNumbers: builder.query<PhoneNumberModel[], any>({
            query: (businessId) => ({
                url: `/Business/GetBusinessPhoneNumbers?businessId=${businessId}`,
                method: 'GET'
            }),
        }),

        // Save phone number
        saveBusinessPhoneNumber: builder.mutation({
            query: (phoneNumberDetails) => ({
                url: '/Business/SaveBusinessPhoneNumber',
                method: 'POST',
                data: phoneNumberDetails,
            }),
        }),

        // Delete phone number
        deletePhoneNumber: builder.mutation({
            query: (businessPhoneNumberId) => ({
                url: `/PhoneConfig/DeletePhoneNumber?businessPhoneNumberId=${businessPhoneNumberId}`,
                method: 'POST'
            }),
        }),

        // List available phone numbers
        listAvailablePhoneNumbers: builder.query<AvailablePhoneNumberModel[], any>({
            query: ({ countryCode, locality }) => ({
                url: `/PhoneConfig/ListAvailablePhoneNumbers?countryCode=${countryCode}&locality=${locality}`,
                method: 'GET'
            }),
        }),
        getTwilioToken: builder.query<any, any>({
            query: (identity) => ({
                url: `/Twilio/GetTwilioToken?identity=${encodeURIComponent(identity)}`,
                method: 'GET'
            }),
        }),
        // Create phone number
        createPhoneNumber: builder.mutation<any, any>({
            query: ({ uniqueName, phoneNumber, businessId }) => ({
                url: `/PhoneConfig/CreatePhoneNumber?uniqueName=${uniqueName}&phoneNumber=${phoneNumber}&businessId=${businessId}`,
                method: 'POST'
            }),
        }),

        createBusinessDomain: builder.mutation<BusinessDomainInformation, string>({
            query: (body) => ({
                url: `/BusinessDomain/CreateBusinessDomain?businessType=${body}`,
                method: 'POST',
                data: body
            }),
        }),

        saveNewBusiness: builder.mutation<BusinessDataModel, Partial<BusinessDataModel>>({
            query: (body) => ({
                url: '/App/SaveNewBusiness',
                method: 'POST',
                data: body
            }),
        }),

        // Business
        saveBusiness: builder.mutation({
            query: (business) => ({
                url: '/Business/SaveBusiness',
                method: 'POST',
                data: business,
            }),
        }),
        setBusinessOfflineStatus: builder.mutation<BusinessModel, { isOffline: boolean }>({
            query: (body) => ({
                url: '/Business/SetOfflineStatus',
                method: 'POST',
                data: body,
            }),
        }),
        setBusinessSetupComplete: builder.mutation<BusinessModel, { setupComplete: boolean }>({
            query: (body) => ({
                url: '/Business/SetSetupComplete',
                method: 'POST',
                data: body,
            }),
        }),
        deleteBusinessAndAccount: builder.mutation<void, void>({
            query: () => ({
                url: '/Business/DeleteBusinessAndAccount',
                method: 'DELETE',
            }),
        }),
        saveCustomer: builder.mutation<CustomerModel, CustomerModel>({
            query: (body) => ({
                url: '/Business/SaveCustomer',
                method: 'POST',
                data: body,
            }),
        }),
        importCustomersCsv: builder.mutation<CustomerCsvImportResultModel, File>({
            query: (file) => {
                const formData = new FormData();
                formData.append('file', file);

                return {
                    url: '/Business/ImportCustomersCsv',
                    method: 'POST',
                    data: formData,
                };
            },
        }),
        saveAddress: builder.mutation<AddressModel, AddressModel>({
            query: (body) => ({
                url: '/Business/SaveAddress',
                method: 'POST',
                data: body,
            }),
        }),
        saveContact: builder.mutation<ContactModel, ContactModel>({
            query: (body) => ({
                url: '/Business/SaveContact',
                method: 'POST',
                data: body
            }),
        }),
        saveEmployee: builder.mutation<EmployeeModel, EmployeeModel>({
            query: (body) => ({
                url: '/Business/SaveEmployee',
                method: 'POST',
                data: body
            }),
        }),
        saveBusinessHour: builder.mutation({
            query: (body) => ({
                url: '/Business/SaveBusinessHour',
                method: 'POST',
                data: body
            }),
        }),
        saveBusinessHours: builder.mutation<BusinessHourModel[], BusinessHourModel[]>({
            query: (body) => ({
                url: '/Business/SaveBusinessHours',
                method: 'POST',
                data: body
            }),
        }),
        saveBooking: builder.mutation<BookingSaveModel, BookingSaveModel>({
            query: (body) => ({
                url: '/Business/SaveBooking',
                method: 'POST',
                data: body,
            }),
        }),
        getConversationMeta: builder.query<{ recordCount: number, conversationMetadata: ConversationMetaModel[] }, ConversationMetaFilterModel>({
            query: (filters) => ({
                url: '/Conversation/GetConversationMeta',
                method: 'GET',
                params: filters
            }),
        }),
        getConversationMetaByCustomerId: builder.query<ConversationMetaModel[], { businessId: number; customerId: number }>({
            query: ({ businessId, customerId }) => ({
                url: `/Conversation/GetConversationMetaByCustomerId`,
                method: 'GET',
                params: { businessId, customerId }
            }),
        }),
        getConversationByConversationMetaId: builder.query<TranscriptMessageModel[], { businessId: number, conversationMetaId: number }>({
            query: ({ businessId, conversationMetaId }) => ({
                url: `/Conversation/GetConversationByConversationMetaId?businessId=${businessId}&conversationMetaId=${conversationMetaId}`,
                method: 'GET'
            }),
        }),
        getConversationReview: builder.query<ConversationReviewModel, { businessId: number; conversationMetaId: number }>({
            query: ({ businessId, conversationMetaId }) => ({
                url: `/Conversation/GetConversationReview`,
                method: 'GET',
                params: { businessId, conversationMetaId }
            }),
        }),
        getRecording: builder.query<Blob, { businessId: number; conversationMetaId: number }>({
            query: ({ businessId, conversationMetaId }) => ({
                url: `/Recording/GetRecording`,
                method: 'GET',
                params: { businessId, conversationMetaId },
                responseType: 'blob'
            }),
        }),
        getDashboardMetrics: builder.query<DashboardMetricsModel, { businessId: number; from: string; to: string }>({
            query: ({ businessId, from, to }) => ({
                url: `/Dashboard/GetMetrics`,
                method: 'GET',
                params: { businessId, from, to }
            }),
        }),
        getDashboardNeedsAttention: builder.query<ConversationMetaModel[], { businessId: number; from: string; to: string; take?: number }>({
            query: ({ businessId, from, to, take }) => ({
                url: `/Dashboard/GetNeedsAttention`,
                method: 'GET',
                params: { businessId, from, to, take }
            }),
        }),
        getDashboardActionCommunications: builder.query<DashboardActionCommunicationsResponseModel, { businessId: number; from: string; to: string; take?: number }>({
            query: ({ businessId, from, to, take }) => ({
                url: `/Dashboard/GetActionCommunications`,
                method: 'GET',
                params: { businessId, from, to, take }
            }),
        }),
        getWorkItemsOpen: builder.query<WorkItemModel[], { businessId: number; status?: string; from: string; to: string; pageSize?: number; offset?: number }>({
            query: ({ businessId, status = 'open', from, to, pageSize = 20, offset = 0 }) => ({
                url: `/WorkItem/ListOpen`,
                method: 'GET',
                params: { businessId, status, from, to, pageSize, offset }
            }),
        }),
        getWorkItemsByConversationMetaId: builder.query<WorkItemModel[], { businessId: number; conversationMetaId: number }>({
            query: ({ businessId, conversationMetaId }) => ({
                url: `/WorkItem/GetByConversationMetaId`,
                method: 'GET',
                params: { businessId, conversationMetaId }
            }),
        }),
        getSupportTickets: builder.query<SupportTicketModel[], { businessId: number; status?: string }>({
            query: ({ businessId, status = '' }) => ({
                url: `/SupportTicket/ListByBusiness`,
                method: 'GET',
                params: { businessId, status }
            }),
        }),
        getSupportTicketDetail: builder.query<SupportTicketDetailModel, { businessId: number; supportTicketId: number }>({
            query: ({ businessId, supportTicketId }) => ({
                url: `/SupportTicket/GetDetail`,
                method: 'GET',
                params: { businessId, supportTicketId }
            }),
        }),
        createSupportTicket: builder.mutation<number | null, { businessId: number; category: string; subject: string; description: string }>({
            query: (body) => ({
                url: `/SupportTicket/Create`,
                method: 'POST',
                data: body
            }),
        }),
        updateSupportTicketStatus: builder.mutation<boolean, { businessId: number; supportTicketId: number; status: string }>({
            query: (body) => ({
                url: `/SupportTicket/UpdateStatus`,
                method: 'POST',
                data: body
            }),
        }),
        addSupportTicketComment: builder.mutation<number | null, { businessId: number; supportTicketId: number; commentText: string; authorType: string; authorName?: string }>({
            query: (body) => ({
                url: `/SupportTicket/AddComment`,
                method: 'POST',
                data: body
            }),
        }),
        updateWorkItemStatus: builder.mutation<boolean, { workItemId: number; status: string }>({
            query: (body) => ({
                url: `/WorkItem/UpdateStatus`,
                method: 'POST',
                data: body
            }),
        }),
        addWorkItemComment: builder.mutation<number | null, { workItemId: number; commentText: string; visibility?: string }>({
            query: ({ visibility = 'internal', ...body }) => ({
                url: `/WorkItem/AddComment`,
                method: 'POST',
                data: { ...body, visibility }
            }),
        }),
        setLiveListen: builder.mutation<boolean, { businessId: number; callSid: string; enabled: boolean }>({
            query: ({ businessId, callSid, enabled }) => ({
                url: `/Conversation/SetLiveListen`,
                method: 'POST',
                params: { businessId, callSid, enabled }
            }),
        }),
        startCallbackCall: builder.mutation<{ callSid: string }, { businessId: number; conversationMetaId: number }>({
            query: ({ businessId, conversationMetaId }) => ({
                url: `/Conversation/StartCallbackCall`,
                method: 'POST',
                params: { businessId, conversationMetaId }
            }),
        }),
        endTwilioCall: builder.mutation<boolean, { businessId: number; callSid: string }>({
            query: ({ businessId, callSid }) => ({
                url: `/Conversation/EndTwilioCall`,
                method: 'POST',
                params: { businessId, callSid }
            }),
        }),
        getBookings: builder.query<{ recordCount: number, bookings: BookingModel[] }, BookingFilterModel>({
            query: (filters) => ({
                url: '/Booking/GetBookings',
                method: 'GET',
                params: filters
            }),
        }),
        deleteConversationMeta: builder.mutation<boolean, { businessId: number; conversationMetaId: number }>({
            query: ({ businessId, conversationMetaId }) => ({
                url: `/Conversation/DeleteConversationMeta`,
                method: 'DELETE',
                params: { businessId, conversationMetaId }
            }),
        }),

        getBaseBookings: builder.query<BookingModel[], any>({
            query: (businessId) => ({
                url: `/Booking/GetBaseBookings?businessId=${businessId}`,
                method: 'GET',
                data: null
            })
        }),
        getActiveBookings: builder.mutation<BookingModel[], BookingFilterModel>({
            query: (filters) => ({
                url: `/Booking/GetActiveBookingsByPeriod`,
                method: 'POST',
                data: filters
            }),
        }),
        getCancelledBookings: builder.mutation<BookingModel[], BookingFilterModel>({
            query: (filters) => ({
                url: `/Booking/GetCancelledBookingsByPeriod`,
                method: 'POST',
                data: filters
            }),
        }),
        getCompletedBookings: builder.mutation<BookingModel[], BookingFilterModel>({
            query: (filters) => ({
                url: `/Booking/GetCompletedBookingsByPeriod`,
                method: 'POST',
                data: filters
            }),
        }),
        getAllBookings: builder.mutation<BookingModel[], BookingFilterModel>({
            query: (filters) => ({
                url: `/Booking/GetAllBookingsByPeriod`,
                method: 'POST',
                data: filters
            }),
        }),
        getBusiness: builder.query<BusinessModel, void>({
            query: () => ({
                url: '/Business/GetBusiness',
                method: 'GET',
                data: null
            }),
        }),
        getCustomers: builder.query<CustomerModel[], number>({
            query: (businessId) => ({
                url: `/Business/GetCustomers?businessId=${businessId}`,
                method: 'GET',
                data: null
            }),
        }),
        getAddresses: builder.query<AddressModel[], number>({
            query: (businessId) => ({
                url: `/Business/GetAddresses?businessId=${businessId}`,
                method: 'GET',
                data: null
            }),
        }),
        getContacts: builder.query<ContactModel[], number>({
            query: (businessId) => ({
                url: `/Business/GetContacts?businessId=${businessId}`,
                method: 'GET',
                data: null
            }),
        }),
        getEmployees: builder.query<EmployeeModel[], number>({
            query: (businessId) => ({
                url: `/Business/GetEmployees?businessId=${businessId}`,
                method: 'GET',
                data: null
            }),
        }),
        getBusinessPhoneNumber: builder.query<PhoneNumberModel, any>({
            query: () => ({
                url: '/Business/GetBusinessPhoneNumber',
                method: 'GET',
                data: null
            }),
        }),
        getBusinessHour: builder.query<BusinessHourModel[], number>({
            query: (businessId) => ({
                url: '/Business/GetBusinessHour',
                method: 'GET',
                params: { businessId }
            }),
        }),
        deleteAddress: builder.mutation({
            query: (addressId) => ({
                url: `/Business/DeleteAddress?addressId=${addressId}`,
                method: 'DELETE',
                responseHandler: (response: any) => response.text()
            }),
        }),
        deleteContact: builder.mutation({
            query: (contactId) => ({
                url: `/Business/DeleteContact?contactId=${contactId}`,
                method: 'DELETE',
                responseHandler: (response: any) => response.text()
            }),
        }),
        deleteEmployee: builder.mutation({
            query: (employeeId) => ({
                url: `/Business/DeleteEmployee?employeeId=${employeeId}`,
                method: 'DELETE',
                responseHandler: (response: any) => response.text()
            }),
        }),
        deleteBusinessHour: builder.mutation({
            query: (businessHourId) => ({
                url: `/Business/DeleteBusinessHour?businessHourId=${businessHourId}`,
                method: 'DELETE',
                responseHandler: (response: any) => response.text()
            }),
        }),
        // Business Domain
        saveAnswer: builder.mutation<AnswerModel, AnswerModel>({
            query: (answer) => ({
                url: '/BusinessDomain/SaveAnswer',
                method: 'POST',
                data: answer,
            }),
        }),
        getBusinessDomains: builder.query<BusinessDomainModel[], void>({
            query: () => ({
                url: '/BusinessDomain/GetBusinessDomains',
                method: 'GET',
                data: null
            }),
        }),
        getSubDomains: builder.query<SubDomainModel[], number>({
            query: (businessDomainId) => ({
                url: `/BusinessDomain/GetSubDomains?businessDomainId=${businessDomainId}`,
                method: 'GET',
                data: null
            }),
        }),
        getSecretaryTasks: builder.query<SecretaryTaskModel[], number>({
            query: (businessDomainId) => ({
                url: `/BusinessDomain/GetSecretaryTasks?businessDomainId=${businessDomainId}`,
                method: 'GET',
                data: null
            }),
        }),
        getQuestions: builder.query<QuestionModel[], number>({
            query: (secretaryTaskId) => ({
                url: `/BusinessDomain/GetQuestions?secretaryTaskId=${secretaryTaskId}`,
                method: 'GET',
                data: null
            }),
        }),
        getSecretaryTasksAndQuestions: builder.query<SecretaryTasksAndQuestionsModel, number>({
            query: (businessDomainId) => ({
                url: `/BusinessDomain/GetSecretaryTasksAndQuestions?businessDomainId=${businessDomainId}`,
                method: 'GET',
                data: null
            }),
        }),
        createTasksAndQuestions: builder.mutation<SecretaryTasksAndQuestionsAndAnswersModel, any>({
            query: ({ businessDomainId, subDomainId, businessId }) => ({
                url: `/BusinessDomain/GetSecretaryTasksAndQuestionsAndGenerateAnswers?businessDomainId=${businessDomainId}&subDomainId=${subDomainId}&businessId=${businessId}`,
                method: 'GET'
            }),
        }),
        saveAllAnswers: builder.mutation<AnswerModel[], AnswerModel[]>({
            query: (answers) => ({
                url: '/BusinessDomain/SaveAllAnswers',
                method: 'POST',
                data: answers
            }),
        }),
        createBusinessAnswer: builder.mutation<AnswerModel, CreateBusinessAnswerModel>({
            query: (body) => ({
                url: '/BusinessDomain/CreateBusinessAnswer',
                method: 'POST',
                data: body
            }),
        }),
        getAnswers: builder.query<AnswerModel[], number>({
            query: (businessId) => ({
                url: `/BusinessDomain/GetAnswers?businessId=${businessId}`,
                method: 'GET',
                data: null
            }),
        }),
        deleteAnswer: builder.mutation({
            query: (answerId) => ({
                url: `/BusinessDomain/DeleteAnswer?answerId=${answerId}`,
                method: 'DELETE',
                responseHandler: (response: any) => response.text()
            }),
        }),

        saveBusinessQuestion: builder.mutation({
            query: (businessQuestion) => ({
                url: '/BusinessQuestion/SaveBusinessQuestion',
                method: 'POST',
                data: businessQuestion,
            }),
        }),

        getDetailSpend: builder.mutation({
            query: (data) => ({
                url: '/Billing/GetDetailSpend',
                method: 'POST',
                data: data,
            }),
        }),
        getBillingSummary: builder.query<BillingSummaryModel, number>({
            query: (_businessId) => ({
                url: '/Billing/GetSummary',
                method: 'GET'
            }),
        }),
        getBillingPaymentProviderStatus: builder.query<BillingPaymentProviderStatusModel, number>({
            query: (_businessId) => ({
                url: '/Billing/GetPaymentProviderStatus',
                method: 'GET'
            }),
        }),
        saveBillingBalanceControls: builder.mutation<BillingBalanceControlsSaveResponseModel, BillingBalanceControlsSaveRequestModel>({
            query: (data) => ({
                url: '/Billing/SaveBalanceControls',
                method: 'POST',
                data
            }),
        }),
        createBillingPaymentRequest: builder.mutation<BillingPaymentRequestCreateResponseModel, BillingPaymentRequestCreateRequestModel>({
            query: (data) => ({
                url: '/Billing/CreatePaymentRequest',
                method: 'POST',
                data
            }),
        }),
        createBillingPaymentMethodSetupSession: builder.mutation<BillingPaymentMethodSetupResponseModel, void>({
            query: () => ({
                url: '/Billing/CreatePaymentMethodSetupSession',
                method: 'POST',
            }),
        }),
        removeBillingPaymentMethod: builder.mutation<void, void>({
            query: () => ({
                url: '/Billing/RemovePaymentMethod',
                method: 'POST',
            }),
        }),
        uploadDeepKnowledgePdf: builder.mutation<string, { businessId: number; file: File }>({
            query: ({ businessId, file }) => {
                const formData = new FormData();
                formData.append('file', file);

                return {
                    url: '/DeepKnowledge/UploadPdfAndIndex',
                    method: 'POST',
                    params: { businessId },
                    data: formData,
                };
            },
        }),
        listDeepKnowledgeDocuments: builder.mutation<DeepKnowledgeDocumentModel[], { businessId: number }>({
            query: ({ businessId }) => ({
                url: '/DeepKnowledge/ListDocuments',
                method: 'POST',
                params: { businessId },
            }),
        }),
        deleteDeepKnowledgeDocument: builder.mutation<number, { businessId: number; documentId: string; chunkCount: number }>({
            query: ({ businessId, documentId, chunkCount }) => ({
                url: '/DeepKnowledge/DeleteDocument',
                method: 'POST',
                params: { businessId, documentId, chunkCount },
            }),
        }),
        searchDeepKnowledge: builder.mutation<DeepKnowledgeChunkModel[], { businessId: number; queryText: string; numberOfSemanticMatches: number }>({
            query: ({ businessId, queryText, numberOfSemanticMatches }) => ({
                url: '/DeepKnowledge/Search',
                method: 'POST',
                params: { businessId, queryText, numberOfSemanticMatches },
            }),
        }),
        getLightweightKnowledgeEntries: builder.query<BusinessKnowledgeEntryModel[], number>({
            query: (businessId) => ({
                url: `/LightweightKnowledge/GetEntries?businessId=${businessId}`,
                method: 'GET',
            }),
        }),
        saveLightweightKnowledgeEntry: builder.mutation<BusinessKnowledgeEntryModel, BusinessKnowledgeEntryModel>({
            query: (entry) => ({
                url: '/LightweightKnowledge/SaveEntry',
                method: 'POST',
                data: entry,
            }),
        }),
        deleteLightweightKnowledgeEntry: builder.mutation<void, { businessId: number; businessKnowledgeEntryId: number }>({
            query: ({ businessId, businessKnowledgeEntryId }) => ({
                url: `/LightweightKnowledge/DeleteEntry?businessId=${businessId}&businessKnowledgeEntryId=${businessKnowledgeEntryId}`,
                method: 'DELETE',
            }),
        }),
        searchLightweightKnowledge: builder.mutation<BusinessKnowledgeSearchResponseModel, BusinessKnowledgeSearchRequestModel>({
            query: (request) => ({
                url: '/LightweightKnowledge/Search',
                method: 'POST',
                data: request,
            }),
        }),
        rescanLightweightKnowledgeWebsite: builder.mutation<BusinessKnowledgeWebsiteRescanResponseModel, BusinessKnowledgeWebsiteRescanRequestModel>({
            query: (request) => ({
                url: '/LightweightKnowledge/RescanWebsite',
                method: 'POST',
                data: request,
            }),
        }),
        saveBusinessAnswer: builder.mutation({
            query: (businessAnswer) => ({
                url: '/BusinessQuestion/SaveBusinessAnswer',
                method: 'POST',
                data: businessAnswer,
            }),
        }),
        getBusinessQuestions: builder.query({
            query: (businessId) => ({
                url: `/BusinessQuestion/GetBusinessQuestions?businessId=${businessId}`,
                method: 'GET'
            }),
        }),
        getBusinessAnswers: builder.query({
            query: (businessId) => ({
                url: `/BusinessQuestion/GetBusinessAnswers?businessId=${businessId}`,
                method: 'GET'
            }),
        }),
        deleteBusinessAnswer: builder.mutation({
            query: (businessAnswerId) => ({
                url: `/BusinessQuestion/DeleteBusinessAnswer?businessAnswerId=${businessAnswerId}`,
                method: 'DELETE',
                responseHandler: (response: any) => response.text()
            }),
        }),

        // Subscription
        saveSubscription: builder.mutation({
            query: (subscription: SubscriptionModel) => ({
                url: '/Subscription/SaveSubscription',
                method: 'POST',
                body: subscription,
            }),
        }),
        getSubscriptionTypes: builder.query<SubscriptionTypeModel[], void>({
            query: () => ({
                url: '/Subscription/GetSubscriptionTypes',
                method: 'GET',
            }),
        }),
        getPeriods: builder.query({
            query: (arg) => ({
                url: '/Subscription/GetPeriod',
                method: 'GET',
                data: arg
            }),
        }),

        getSubscriptions: builder.query({
            query: (businessId) => ({
                url: `/Subscription/GetSubscription?businessId=${businessId}`,
                method: 'GET'
            }),
        }),

        // Skills
        getSkillNames: builder.query<string[], number | void>({
            query: (businessId) => ({
                url: `/Skill/GetSkillNames${businessId ? `?businessId=${businessId}` : ''}`,
                method: 'GET',
            }),
        }),
        getSkills: builder.query<SkillSetModel, number | void>({
            query: (businessId) => ({
                url: `/Skill/GetSkills${businessId ? `?businessId=${businessId}` : ''}`,
                method: 'GET',
            }),
        }),
        getSkillByName: builder.query<SkillModel, { skillName: string; businessId?: number }>({
            query: ({ skillName, businessId }) => ({
                url: `/Skill/GetSkillsByName?skillName=${encodeURIComponent(skillName)}${businessId ? `&businessId=${businessId}` : ''}`,
                method: 'GET',
            }),
        }),
        processSkillByName: builder.mutation<boolean, { skillName: string; channelId?: string; businessId?: number }>({
            query: ({ skillName, channelId, businessId }) => {
                const queryParts = [
                    `skillName=${encodeURIComponent(skillName)}`,
                    channelId ? `channelId=${encodeURIComponent(channelId)}` : null,
                    businessId ? `businessId=${businessId}` : null
                ].filter(Boolean).join('&');

                return {
                    url: `/Skill/ProcessSkillByName?${queryParts}`,
                    method: 'POST',
                    data: null,
                };
            },
        }),
        processSkill: builder.mutation<SkillModel, { skill: SkillModel; channelId?: string; businessId?: number }>({
            query: ({ skill, channelId, businessId }) => {
                const queryParts: string[] = [];
                if (channelId) queryParts.push(`channelId=${encodeURIComponent(channelId)}`);
                if (businessId) queryParts.push(`businessId=${businessId}`);
                const qs = queryParts.length ? `?${queryParts.join('&')}` : '';

                return {
                    url: `/Skill/ProcessSkill${qs}`,
                    method: 'POST',
                    data: skill,
                };
            },
        }),
        saveSkill: builder.mutation<boolean, { skill: SkillModel; businessId?: number }>({
            query: ({ skill, businessId }) => ({
                url: `/Skill/SaveSkill${businessId ? `?businessId=${businessId}` : ''}`,
                method: 'POST',
                data: skill,
            }),
        }),
        deleteSkill: builder.mutation<boolean, { skillName: string; businessId?: number }>({
            query: ({ skillName, businessId }) => ({
                url: `/Skill/DeleteSkill?skillName=${encodeURIComponent(skillName)}${businessId ? `&businessId=${businessId}` : ''}`,
                method: 'DELETE',
                data: null,
            }),
        }),
        renameSkill: builder.mutation<boolean, { currentSkillName: string; newSkillName: string; businessId?: number }>({
            query: ({ currentSkillName, newSkillName, businessId }) => ({
                url: `/Skill/RenameSkill?currentSkillName=${encodeURIComponent(currentSkillName)}&newSkillName=${encodeURIComponent(newSkillName)}${businessId ? `&businessId=${businessId}` : ''}`,
                method: 'POST',
                data: null,
            }),
        }),
        downloadProcessedEmailCsv: builder.mutation<Blob, number>({
            query: (businessId) => ({
                url: `/Skill/DownloadProcessedEmailCsv?businessId=${businessId}`,
                method: 'GET',
                responseType: 'blob',
            }),
        }),
    })
})

// // Add a response interceptor
axios.interceptors.response.use(
    function (response) {
        return response;
    },
    function (error) {

        if (error && error.response) {
            if (error.response.status === 404) {
                return 'No data found';
            }

            // if (error.response.status === 401) {
            //     alert("Your session has expired. Please login again.");

            //     logout();
            //     return Promise.resolve(error);
            // }
        }
        return Promise.reject(error);
    }
);

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
    useGetCustomersQuery, useGetEmployeesQuery,
    useLazyGetPortalBusinessDataQuery, useGetCallLogQuery, useGetAppVersionQuery, useLazyGetCallLogQuery, useSubmitTailoredSetupInquiryMutation, useCompleteSimpleSetupMutation, useLazySearchQuickSetupGooglePlacesQuery, useLazyGetQuickSetupGooglePlaceQuery, useGetSetupFreeTestCallAllowanceQuery, useLazyGetSetupFreeTestCallAllowanceQuery, useGetAgencyBusinessesQuery, useLazyGetAgencyBusinessesQuery, useSwitchAgencyBusinessMutation, useSaveBookingMutation,
    useLazyGetConversationMetaQuery, useLazyGetConversationMetaByCustomerIdQuery, useLazyGetConversationByConversationMetaIdQuery, useLazyGetConversationReviewQuery, useLazyGetRecordingQuery,
    useLazyGetDashboardMetricsQuery, useLazyGetDashboardNeedsAttentionQuery, useLazyGetDashboardActionCommunicationsQuery, useLazyGetWorkItemsOpenQuery, useLazyGetWorkItemsByConversationMetaIdQuery,
    useGetSupportTicketsQuery, useLazyGetSupportTicketsQuery, useGetSupportTicketDetailQuery, useLazyGetSupportTicketDetailQuery,
    useCreateSupportTicketMutation, useUpdateSupportTicketStatusMutation, useAddSupportTicketCommentMutation,
    useUpdateWorkItemStatusMutation, useAddWorkItemCommentMutation, useSetLiveListenMutation, useStartCallbackCallMutation, useEndTwilioCallMutation,
    useLazyGetBookingsQuery, useDeleteConversationMetaMutation, useCreateBusinessAnswerMutation, useGetAiTestCallerSetupQuery, useLazyGetAiTestCallerSetupQuery, useSaveAiTestCallerSetupMutation, useGenerateSimulatedCallerReplyMutation, useGenerateSimulatedCallerSpeechMutation, useGenerateVoicePreviewSpeechMutation,
    useAskMutation, useGetPromptVersionsQuery, useLazyGetPromptVersionsQuery, useGetPromptVersionQuery, useLazyGetPromptVersionQuery, useSavePromptBatchMutation, useSavePromptPackageMutation, useSaveDraftDataMutation, useClearDraftDataMutation, useLazyGetDraftDataQuery, useLazyGetPromptsAndAssistantsQuery, useLazyGetPromptTagDefinitionsQuery, useLazyGetDpaIdentityConfigQuery, useSaveDpaIdentityConfigMutation, useTestDpaVerificationConnectorMutation, useLazyGetBusinessNotificationConfigQuery, useSaveBusinessNotificationConfigMutation, useLazyGetPromptServicesQuery, useRevertPromptMutation, useCreateFlowPromptMutation, useDeleteFlowPromptMutation, useClearCallContextMutation, useGetAllBookingsMutation, useLazyGetCustomersQuery,
    useLazyListAvailablePhoneNumbersQuery, useLazyGetTwilioTokenQuery, useCreatePhoneNumberMutation, useSaveNewBusinessMutation, useGetBusinessDomainsQuery, useLazyGetSubDomainsQuery, useDeleteAddressMutation, useDeleteAnswerMutation, useDeleteBusinessAnswerMutation,
    useDeleteContactMutation, useLazyGetAddressesQuery, useLazyGetAnswersQuery, useGetBusinessAnswersQuery, useGetBusinessHourQuery, useLazyGetBusinessPhoneNumbersQuery, useDeletePhoneNumberMutation,
    useGetBusinessQuery, useGetBusinessQuestionsQuery, useLazyGetContactsQuery, useLazyGetEmployeesQuery, useGetPeriodsQuery, useGetQuestionsQuery, useLazyGetSecretaryTasksQuery,
    useGetSubscriptionTypesQuery, useGetSubscriptionsQuery, useSaveAddressMutation, useSaveCustomerMutation, useImportCustomersCsvMutation, useSaveAnswerMutation, useSaveBusinessAnswerMutation, useSaveBusinessHourMutation, useSaveBusinessHoursMutation,
    useSaveBusinessMutation, useSetBusinessOfflineStatusMutation, useSetBusinessSetupCompleteMutation, useDeleteBusinessAndAccountMutation, useSaveBusinessPhoneNumberMutation, useSaveBusinessQuestionMutation, useSaveContactMutation, useSaveEmployeeMutation, useSaveSubscriptionMutation,
    useDeleteEmployeeMutation, useDeleteBusinessHourMutation, useLazyGetQuestionsQuery, useLazyGetSecretaryTasksAndQuestionsQuery, useSaveServiceMutation, useGetServicesQuery, useCreateBusinessDomainMutation, useCreateTasksAndQuestionsMutation, useAuthenticateGoogleLoginMutation, useRegisterUsernameAndPasswordMutation, useAuthenticateUsernameAndPasswordMutation, useForgotPasswordUsernameAndPasswordMutation, useResetPasswordUsernameAndPasswordMutation, useChangePasswordMutation, useSaveAllAnswersMutation,
    useLazyGetClaimsQuery, useLazyGetPoliciesQuery, useLazyGetIncidentsQuery, useGetDetailSpendMutation, useGetBillingSummaryQuery, useLazyGetBillingSummaryQuery, useGetBillingPaymentProviderStatusQuery, useLazyGetBillingPaymentProviderStatusQuery, useSaveBillingBalanceControlsMutation, useCreateBillingPaymentRequestMutation, useCreateBillingPaymentMethodSetupSessionMutation, useRemoveBillingPaymentMethodMutation, useUploadDeepKnowledgePdfMutation, useListDeepKnowledgeDocumentsMutation, useDeleteDeepKnowledgeDocumentMutation, useSearchDeepKnowledgeMutation, useGetLightweightKnowledgeEntriesQuery, useSaveLightweightKnowledgeEntryMutation, useDeleteLightweightKnowledgeEntryMutation, useSearchLightweightKnowledgeMutation, useRescanLightweightKnowledgeWebsiteMutation, useGetSkillNamesQuery, useLazyGetSkillNamesQuery, useGetSkillsQuery, useLazyGetSkillsQuery,
    useGetSkillByNameQuery, useLazyGetSkillByNameQuery, useProcessSkillByNameMutation, useProcessSkillMutation, useSaveSkillMutation, useDeleteSkillMutation, useRenameSkillMutation, useDownloadProcessedEmailCsvMutation
} = apiSlice
